import React from "react";
import t from "../../../i18n";
import ModalForm from "@ui/Modal/Form";
import { analysisTitleLong } from "./analysisTitle";
import type { Analysis } from "../../../api/Analysis";
import type { Project } from "../../../api/useProjects";
import { css } from "@linaria/core";
import type { Variant } from "@ui/Button";
import { usePost } from "../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";

export type PublicationAction = "publish" | "unpublish";

interface Props {
  closeModal: () => void;
  action: PublicationAction;
  selectedAnalyses: Analysis[];
  projectId: Project["id"];
  projectTimezone: Project["timezone"];
}

const publicationEndpoints = {
  publish: "/projects/:projectId(uuid)/analyses/publish",
  unpublish: "/projects/:projectId(uuid)/analyses/unpublish",
} as const;

const okButtonVariant: Record<PublicationAction, Variant> = {
  publish: "primary",
  unpublish: "destructive",
};

const okButtonName: Record<PublicationAction, string> = {
  publish: t("commons.button.publish"),
  unpublish: t("commons.button.unpublish"),
};
/**
 * Reset the list style to disc, with custom padding aligment
 */
const AnalysisList = css`
  list-style: initial;
  padding-left: 17px;
`;

/**
 *
 * @param Props
 * @param Props.closeModal Close modal function handler
 * @param Props.action Action to perform. The modal accept either publish or unpublish
 * @param Props.selectedAnalyses List of analyses to publish or unpublish
 * @param Props.projectTimezone Project timezone, to format the analyses listing
 * @returns
 */
function PublishUnpublishModalForm({
  closeModal,
  action,
  selectedAnalyses,
  projectId,
  projectTimezone,
}: Props) {
  const analysisIds = React.useMemo(() => selectedAnalyses.map(({ id }) => id), [selectedAnalyses]);
  const toggleAnalysesPublication = usePost(publicationEndpoints[action]);
  const { addToast } = useToasts();
  const handleSubmit = React.useCallback(async () => {
    await toggleAnalysesPublication({
      routeParams: { projectId },
      body: analysisIds,
    });
    addToast(
      "success",
      t("project.charts.publish_analysis.success", { count: analysisIds.length, action }),
    );
    return true;
  }, [action, addToast, analysisIds, projectId, toggleAnalysesPublication]);

  return (
    <ModalForm
      cancellable
      onRequestClose={closeModal}
      onSubmit={handleSubmit}
      okButtonVariant={okButtonVariant[action]}
      okButtonName={okButtonName[action]}
    >
      <ul className={AnalysisList}>
        {selectedAnalyses.map(({ id, label, date, group }) => (
          <li key={id}>{analysisTitleLong({ label, date, group }, projectTimezone)}</li>
        ))}
      </ul>
    </ModalForm>
  );
}

export default React.memo(PublishUnpublishModalForm);
